import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  AppBar,
  IconButton,
  Avatar,
  Badge,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import HomeIcon from '@mui/icons-material/Home';
import RateReviewIcon from '@mui/icons-material/RateReview';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MessageIcon from '@mui/icons-material/Message';

import MessageBoxWithPopup from './MessageBox';
import ProductReviewForm from './ProductReviewForm';
import HomePage from './HomePage';
import ResellerProfile from './ResellerProfile';
import ReviewHistory from './ReviewHistory';
import NotificationsPage from './NotificationsPage';
import ResendOfferTable from './ResendOfferTable';

const drawerWidth = 240;

const SidebarWithSwitch = () => {
  const [open, setOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState('home');
  const [reseller, setReseller] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const stored = localStorage.getItem('reseller');
    if (stored) {
      setReseller(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (reseller?.resellerId) {
        try {
          const res = await fetch(`https://reviewhub-lc.onrender.com/api/notification/${reseller.resellerId}`);
          const data = await res.json();
          setNotifications(data);
        } catch (err) {
          console.error('Error fetching notifications', err);
        }
      }
    };
    fetchNotifications();
  }, [reseller]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleMenuClick = (page) => {
    if (page === 'logout') {
      const confirmLogout = window.confirm('Are you sure you want to logout?');
      if (confirmLogout) {
        localStorage.clear();
        window.location.href = '/reseller-login';
      }
    } else {
      setSelectedPage(page);
      if (isMobile) setOpen(false);
    }
  };

  const markAsRead = async (id) => {
    await fetch(`https://reviewhub-lc.onrender.com/api/notification/${id}/read`, {
      method: 'PATCH',
    });
    setNotifications((prev) =>
      prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
    );
  };

  const menuItems = [
    { text: 'Home Page', icon: <HomeIcon />, page: 'home' },
    { text: 'Review Form', icon: <RateReviewIcon />, page: 'form' },
    { text: 'Resend Offer', icon: <LocalOfferIcon />, page: 'resend' },
    { text: 'Review History', icon: <HistoryIcon />, page: 'history' },
    { text: 'Profile', icon: <PersonIcon />, page: 'profile' },
    { text: 'Messages', icon: <MessageIcon />, page: 'messages' },
    { text: 'Logout', icon: <LogoutIcon />, page: 'logout' },
  ];

  const renderPage = () => {
    switch (selectedPage) {
      case 'form':
        return <ProductReviewForm />;
      case 'resend':
        return <ResendOfferTable />;
      case 'history':
        return <ReviewHistory resellerId={reseller?.resellerId} />;
      case 'profile':
        return <ResellerProfile />;
      case 'notifications':
        return (
          <NotificationsPage
            notifications={notifications}
            markAsRead={markAsRead}
          />
        );
      case 'messages':
        return <MessageBoxWithPopup />;
      case 'home':
      default:
        return <HomePage />;
    }
  };

  const drawerContent = (
    <Box sx={{ overflow: 'auto' }}>
      <Toolbar />
      <List>
        {menuItems.map(({ text, icon, page }) => (
          <ListItem key={text} disablePadding>
            <ListItemButton
              onClick={() => handleMenuClick(page)}
              selected={selectedPage === page}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: '#757575',
                  color: '#fff',
                  '& .MuiListItemIcon-root': {
                    color: '#fff',
                  },
                },
                '&:hover': {
                  backgroundColor: '#616161',
                  color: '#fff',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>{icon}</ListItemIcon>
              <ListItemText primary={text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#424242',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" color="inherit" onClick={handleToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap>
              Review Dashboard
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton color="inherit" onClick={() => setSelectedPage('notifications')}>
              <Badge
                badgeContent={notifications.filter((n) => !n.isRead).length}
                color="error"
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
            {reseller && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!isMobile && (
                  <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {reseller.resellerId}
                  </Typography>
                )}
                <Avatar
                  alt="Profile"
                  src={reseller.profileImageUrl || '/assets/defaultProfile.jpg'}
                  sx={{ width: 40, height: 40, border: '2px solid #fff' }}
                />
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        onClose={handleToggle}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#616161',
            color: '#FAFAFA',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: '#EEEEEE',
          minHeight: '100vh',
          p: { xs: 1, sm: 3 },
          ...(isMobile
            ? {}
            : {
                ml: open ? `${drawerWidth}px` : 0,
                transition: (theme) =>
                  theme.transitions.create('margin', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
              }),
        }}
      >
        <Toolbar />
        {renderPage()}
      </Box>
    </Box>
  );
};

export default SidebarWithSwitch;